import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import jsCookie from 'js-cookie';
import { useRouter } from 'next/router';
import React, { useCallback, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { trackMixpanelEvent } from 'utils/analytics';

import {
  CompanyFields,
  StepContainer,
  StepOne,
  StepsActionsContainer,
  StepThree,
  StepTwo,
} from '@/modules/auth';
import { useCompleteSignup } from '@/modules/auth/apiHooks';
import { StepFour } from '@/modules/auth/components/FormsContainer/CreateCompany/Steps/StepFour';
import { useSignup } from '@/modules/auth/components/FormsContainer/Signup/logic';
import { IAuth } from '@/modules/auth/types';
import { useCreatePropertyField } from '@/modules/settings/property/apiHooks';
import LogoutItem from '@/shared/components/molecules/SideMenu/ProfileMenuList/Logout';
import { areaUnits } from '@/shared/constant/general';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';

import Header from './Header';

import Arrow from '~/images/shared/arrow-down.svg';

const CreateCompany = () => {
  const { t } = useTranslation('login');
  const { login, user } = useAuth();
  const router = useRouter();
  const [stepIndex, setStepIndex] = useState(0);

  const BackButton = useCallback(
    () => (
      <div
        className='flex cursor-pointer items-center gap-1 font-medium'
        onClick={() => setStepIndex((idx) => idx - 1)}
      >
        <span className='rotate-90'>
          <Arrow />
        </span>
        <span>
          {t('back', {
            ns: 'common',
          })}
        </span>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const nextStepHandler = () => {
    setStepIndex((idx) => idx + 1);
  };

  const {
    partialSignupFormValidationSchema,
    completeSignupFormValidationSchema,
  } = useSignup();
  const formMethods = useForm<IAuth.ICompleteSignupForm>({
    resolver: yupResolver(
      stepIndex === 4
        ? partialSignupFormValidationSchema
        : completeSignupFormValidationSchema
    ),
    criteriaMode: 'all',
    reValidateMode: 'onChange',
    mode: 'all',
    defaultValues: {
      areaUnit: areaUnits.map((unit) => ({
        label: t(unit),
        value: unit,
      }))[0],
    },
  });

  const { mutate: addFieldMutation, isLoading: isAddingField } =
    useCreatePropertyField();
  const { mutate: signUpMutation, isLoading } = useCompleteSignup();
  const queryClient = useQueryClient();

  // Form submission
  const onSubmit: SubmitHandler<IAuth.ICompleteSignupForm> = (values) => {
    signUpMutation(values, {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['checkCompanyUser']);
        login(data.token, data.user, data.expiry, false);
        trackMixpanelEvent('Pre_Register_su', data.user, {
          isSucces: true,
        });
        nextStepHandler();
      },
      onError: () => {
        trackMixpanelEvent('Pre_Register_su', null, {
          isSucces: false,
        });
      },
    });
  };

  const steps: IAuth.IStepSchema[] = [
    {
      idx: 1,
      title: t('farm-size-question'),
      fields: ['landSize'],
      render: () => <StepOne />,
      backAction: <BackButton />,
      forwardActionhandler: nextStepHandler,
      trackingTitle: 'Start_onboarding_flow_farm_size',
    },
    {
      idx: 2,
      title: t('growing-crops-question'),
      fields: ['cropCategories'],
      render: () => <StepTwo />,
      backAction: <BackButton />,
      forwardActionhandler: nextStepHandler,
      trackingTitle: 'Start_onboarding_flow_get_crop_type',
    },
    {
      idx: 3,
      title: t('aydi-interest-question'),
      fields: ['featuresOfInterest'],
      render: () => <StepThree />,
      backAction: <BackButton />,
      forwardActionhandler: formMethods.handleSubmit(onSubmit),
      isDisabled: isLoading,
      trackingTitle: 'Start_onboarding_flow_client_needs',
    },
    {
      idx: 4,
      title: t('add-your-first-field'),
      fields: ['fieldName', 'fieldType', 'area', 'areaUnit', 'crop'],
      render: () => <StepFour />,
      forwardActionhandler: () => {
        const userObj = JSON.parse(jsCookie.get('userObj') ?? '{}');
        addFieldMutation(
          {
            entityId: userObj?.value?.entityId,
            name: formMethods.getValues('fieldName') as string,
            type: formMethods.getValues('fieldType')?.value as string,
            area: Number(formMethods.getValues('area')),
            areaUnit: formMethods.getValues('areaUnit')?.value as string,
            cropId: Number(formMethods.getValues('crop')?.id),
          },
          {
            onSuccess: () => {
              router.push(routes.home);
              trackMixpanelEvent('signup-add-field', user);
            },
          }
        );
      },
      backAction: <LogoutItem />,
      isDisabled: isLoading || isAddingField,
      trackingTitle: 'Start_onboarding_flow_client_needs',
    },
  ];

  return (
    <div className='flex max-h-screen w-full flex-col gap-2 overflow-hidden overflow-y-auto p-24'>
      <Header />
      <FormProvider {...formMethods}>
        <form className='flex h-full w-full flex-col justify-between gap-1'>
          <div>
            {stepIndex === 0 ? (
              <CompanyFields />
            ) : (
              <StepContainer
                step={steps[stepIndex - 1]}
                stepsCount={steps.length}
              />
            )}
          </div>
          <StepsActionsContainer
            step={
              stepIndex === 0
                ? {
                    fields: [
                      'firstName',
                      'lastName',
                      'companyName',
                      'jobTitle',
                      'phoneNumber',
                      'noOfEmployees',
                    ],
                    backAction: <LogoutItem />,
                    idx: 0,
                    forwardActionhandler: nextStepHandler,
                    trackingTitle: 'Start_onboarding_flow_get_started',
                  }
                : steps[stepIndex - 1]
            }
            stepsCount={steps.length}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export { CreateCompany };
